import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  Upload,
  CheckCircle2,
  AlertCircle,
  Paperclip,
  X,
} from "lucide-react";
import { getApiBaseUrl } from "../config";
import "./CVSubmissionForm.css";

const PROFESSIONS = [
  "Construction & Trades",
  "Hospitality & Catering",
  "Healthcare & Caregiving",
  "Logistics & Warehousing",
  "Manufacturing & Industrial",
  "Security & Facilities",
  "Domestic & Support",
  "Driver / Heavy Operator",
  "Cleaning & Housekeeping",
  "Retail & Customer Service",
  "Maintenance / Technical",
  "Oil, Gas & Energy",
  "Other",
];

const STEPS = ["You", "Role", "CV"];

const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3 MB
const ACCEPTED = [
  ".pdf",
  ".doc",
  ".docx",
  ".txt",
  ".rtf",
];

const slideVariants = {
  enter: { opacity: 0, x: 32 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -32 },
};

export default function CVSubmissionForm() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    fullName: "",
    phone: "",
    email: "",
    profession: "",
    otherProfession: "",
    location: "",
  });
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const fileRef = useRef(null);
  const dragCounter = useRef(0);
  const [dragging, setDragging] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const validateStep = (idx) => {
    const e = {};
    if (idx === 0) {
      if (!data.fullName.trim()) e.fullName = "Required";
      if (!data.phone.trim()) e.phone = "Required";
      if (data.email && !/^\S+@\S+\.\S+$/.test(data.email))
        e.email = "Invalid email";
    }
    if (idx === 1) {
      if (!data.profession) e.profession = "Pick one";
      if (data.profession === "Other" && !data.otherProfession.trim())
        e.otherProfession = "Tell us your role";
    }
    if (idx === 2) {
      if (!file) e.file = "Please attach your CV";
      else if (file.size > MAX_FILE_SIZE) e.file = "File must be under 3 MB";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (validateStep(step)) setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const onPick = (f) => {
    if (!f) return;
    const okExt = ACCEPTED.some((ext) =>
      f.name.toLowerCase().endsWith(ext),
    );
    if (!okExt) {
      setErrors((e) => ({ ...e, file: "PDF, DOC, DOCX, TXT, or RTF only" }));
      return;
    }
    if (f.size > MAX_FILE_SIZE) {
      setErrors((e) => ({ ...e, file: "Max 3 MB" }));
      return;
    }
    setErrors((e) => ({ ...e, file: undefined }));
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    dragCounter.current = 0;
    const f = e.dataTransfer.files?.[0];
    if (f) onPick(f);
  };

  const submit = async () => {
    if (!validateStep(2)) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("fullName", data.fullName);
      fd.append("phone", data.phone);
      fd.append("profession", data.profession);
      if (data.email) fd.append("email", data.email);
      if (data.otherProfession) fd.append("otherProfession", data.otherProfession);
      if (data.location) fd.append("location", data.location);
      fd.append("cv", file);
      const res = await fetch(`${getApiBaseUrl()}/api/cv-submission`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const json = await res.json();
      setDone({ ok: true, id: json.application_id });
    } catch (err) {
      setDone({ ok: false, message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (done?.ok) {
    return (
      <motion.div
        className="cvf cvf--success"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="cvf__success-icon">
          <CheckCircle2 size={36} strokeWidth={1.6} />
        </div>
        <h3 className="cvf__success-title">Thanks{data.fullName ? `, ${data.fullName.split(" ")[0]}` : ""}.</h3>
        <p className="cvf__success-body">
          We&rsquo;ve received your CV. Our team reviews submissions within
          24&ndash;48 hours and reaches out to candidates we can place.
        </p>
        <p className="cvf__success-meta">
          Reference: <code>{done.id?.slice(0, 8)}</code>
        </p>
      </motion.div>
    );
  }

  return (
    <div className="cvf">
      {/* Stepper */}
      <ol className="cvf__steps" aria-label="Form steps">
        {STEPS.map((label, i) => (
          <li
            key={label}
            className={
              "cvf__step " +
              (i === step ? "is-current" : i < step ? "is-done" : "")
            }
            aria-current={i === step ? "step" : undefined}
          >
            <span className="cvf__step-dot">
              {i < step ? <CheckCircle2 size={14} strokeWidth={2} /> : i + 1}
            </span>
            <span className="cvf__step-label">{label}</span>
          </li>
        ))}
      </ol>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          className="cvf__pane"
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
        >
          {step === 0 && (
            <>
              <h3 className="cvf__pane-title">Tell us about you.</h3>
              <p className="cvf__pane-help">
                Used only for shortlisting and contact. We won&rsquo;t share
                your details.
              </p>

              <label className="cvf__field">
                <span className="cvf__label">Full name</span>
                <input
                  type="text"
                  className={`cvf__input ${errors.fullName ? "is-error" : ""}`}
                  value={data.fullName}
                  onChange={(e) => set("fullName", e.target.value)}
                  autoComplete="name"
                  placeholder="e.g. Grace Kimani"
                />
                {errors.fullName && (
                  <span className="cvf__error">{errors.fullName}</span>
                )}
              </label>

              <div className="cvf__row">
                <label className="cvf__field">
                  <span className="cvf__label">Phone</span>
                  <input
                    type="tel"
                    className={`cvf__input ${errors.phone ? "is-error" : ""}`}
                    value={data.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    autoComplete="tel"
                    placeholder="+254 7xx xxx xxx"
                  />
                  {errors.phone && (
                    <span className="cvf__error">{errors.phone}</span>
                  )}
                </label>

                <label className="cvf__field">
                  <span className="cvf__label">
                    Email <span className="cvf__optional">(optional)</span>
                  </span>
                  <input
                    type="email"
                    className={`cvf__input ${errors.email ? "is-error" : ""}`}
                    value={data.email}
                    onChange={(e) => set("email", e.target.value)}
                    autoComplete="email"
                    placeholder="name@example.com"
                  />
                  {errors.email && (
                    <span className="cvf__error">{errors.email}</span>
                  )}
                </label>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <h3 className="cvf__pane-title">What role are you after?</h3>
              <p className="cvf__pane-help">
                Pick the closest match. You can refine later in conversation.
              </p>

              <div className="cvf__chips">
                {PROFESSIONS.map((p) => (
                  <button
                    type="button"
                    key={p}
                    className={`cvf__chip ${
                      data.profession === p ? "is-selected" : ""
                    }`}
                    onClick={() => set("profession", p)}
                  >
                    {p}
                  </button>
                ))}
              </div>
              {errors.profession && (
                <span className="cvf__error cvf__error--block">
                  {errors.profession}
                </span>
              )}

              {data.profession === "Other" && (
                <label className="cvf__field">
                  <span className="cvf__label">Your role</span>
                  <input
                    type="text"
                    className={`cvf__input ${
                      errors.otherProfession ? "is-error" : ""
                    }`}
                    value={data.otherProfession}
                    onChange={(e) => set("otherProfession", e.target.value)}
                    placeholder="e.g. Heavy crane operator"
                  />
                  {errors.otherProfession && (
                    <span className="cvf__error">{errors.otherProfession}</span>
                  )}
                </label>
              )}

              <label className="cvf__field">
                <span className="cvf__label">
                  Where are you based?{" "}
                  <span className="cvf__optional">(optional)</span>
                </span>
                <input
                  type="text"
                  className="cvf__input"
                  value={data.location}
                  onChange={(e) => set("location", e.target.value)}
                  placeholder="e.g. Nairobi, Kenya"
                />
              </label>
            </>
          )}

          {step === 2 && (
            <>
              <h3 className="cvf__pane-title">Attach your CV.</h3>
              <p className="cvf__pane-help">
                PDF, DOC, DOCX, TXT, or RTF &middot; up to 3 MB.
              </p>

              <div
                className={`cvf__drop ${dragging ? "is-drag" : ""} ${
                  errors.file ? "is-error" : ""
                }`}
                onDragEnter={(e) => {
                  e.preventDefault();
                  dragCounter.current++;
                  setDragging(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  dragCounter.current--;
                  if (dragCounter.current <= 0) setDragging(false);
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                onClick={() => fileRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    fileRef.current?.click();
                  }
                }}
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept={ACCEPTED.join(",")}
                  hidden
                  onChange={(e) => onPick(e.target.files?.[0])}
                />
                {file ? (
                  <div className="cvf__file">
                    <span className="cvf__file-icon" aria-hidden="true">
                      <Paperclip size={18} strokeWidth={1.8} />
                    </span>
                    <span className="cvf__file-name">{file.name}</span>
                    <span className="cvf__file-size">
                      {(file.size / 1024).toFixed(0)} KB
                    </span>
                    <button
                      type="button"
                      className="cvf__file-remove"
                      aria-label="Remove file"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                      }}
                    >
                      <X size={14} strokeWidth={2} />
                    </button>
                  </div>
                ) : (
                  <div className="cvf__drop-prompt">
                    <Upload size={22} strokeWidth={1.6} />
                    <strong>Drop your CV here</strong>
                    <span>or click to browse</span>
                  </div>
                )}
              </div>
              {errors.file && (
                <span className="cvf__error cvf__error--block">
                  <AlertCircle size={14} strokeWidth={2} /> {errors.file}
                </span>
              )}

              <p className="cvf__legal">
                By submitting, you agree to be contacted about relevant
                placement opportunities. We never sell your data.
              </p>
            </>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="cvf__actions">
        {step > 0 ? (
          <button
            type="button"
            className="cvf__btn cvf__btn--ghost"
            onClick={back}
            data-cursor="link"
          >
            <ArrowLeft size={16} strokeWidth={2} />
            Back
          </button>
        ) : (
          <span />
        )}
        {step < STEPS.length - 1 ? (
          <button
            type="button"
            className="cvf__btn cvf__btn--primary"
            onClick={next}
            data-cursor="cta"
          >
            Continue
            <ArrowRight size={16} strokeWidth={2} />
          </button>
        ) : (
          <button
            type="button"
            className="cvf__btn cvf__btn--primary"
            onClick={submit}
            disabled={submitting}
            data-cursor="cta"
          >
            {submitting ? "Submitting…" : "Submit CV"}
            {!submitting && <ArrowRight size={16} strokeWidth={2} />}
          </button>
        )}
      </div>

      {done && !done.ok && (
        <div className="cvf__alert">
          <AlertCircle size={16} strokeWidth={2} />
          {done.message || "Submission failed. Please try again."}
        </div>
      )}
    </div>
  );
}
